import React, { useEffect, useState } from "react";
import {
	getActiveServices,
	getDaysOffline,
	getServicesDaysOffline,
} from "./api/fetchData";
import { BusinessApi } from "./api/businessApi";
import { ReservationApi } from "./api/reservationApi";

import {
	OfflineDays,
	ServiceAvailabilityException,
	Service,
} from "./types/entities";
import { CustomException } from "./types/customException";
import { LangProvider } from "./types/internationalization";
import { LoginContext, MultistepContext } from "./types/context";

import { useMultistep } from "./hooks/useMultistep";
import { useLogin } from "./hooks/useLogin";
import { useWindowResize } from "./hooks/useWindowResize";

import { Main } from "./components/home/Main";
import { Loader } from "./components/home/Loader";
import { ErrorMessage } from "./components/styled/ErrorMessage";

import { Box, Dialog, Paper } from "@mui/material";

import { theme } from "./styles/themeProvider";
import background from "./assets/images/background.jpg";

// Main component of the application
function App() {
	// First we need the active services of the restaurant, the offlinedays if exists and the "user flows"
	// Active services are the restaurant options, like lunch, dinner, a tour...
	const [loading, setLoading] = useState(true);
	const [activeServices, setActiveServices] = useState<Service[]>([]);
	const [offlineDays, setOfflineDays] = useState<OfflineDays[]>([]);
	const [serviceAvailabilityException, setServiceAvailabilityException] =
		useState<ServiceAvailabilityException[]>([]);
	// The user flows are the existent flows to navigate through the application
	// For example: Services -> Find table -> Login -> Details
	const [isTokenFetched, setIsTokenFetched] = useState(false);
	const [apiError, setApiError] = useState<CustomException | null>(null);

	// useMultistep is a hook with the navigation information in the app
	const values = useMultistep();
	const loginValues = useLogin();

	const { isMobile } = useWindowResize();

	useEffect(() => {
		const fetchToken = async () => {
			try {
				//const businessApi = new BusinessApi();
				//await businessApi.fetchAndStoreToken();
				setIsTokenFetched(true);
			} catch (error) {
				console.error("Error fetching the token:", error);
				setApiError(error);
			}
		};

		fetchToken();
	}, []);

	// Once the token is fetched, call the hooks
	useEffect(() => {
		if (isTokenFetched) {
			const fetchData = async () => {
				const getExistingReservation = async (paymentId: string) => {
					const reservationApi = new ReservationApi();
					const response = await reservationApi.getReservationByPayment(
						paymentId
					);
					let existingReservation = response.data;
					if (existingReservation) {
						const date = new Date(existingReservation.time!);

						existingReservation = {
							...existingReservation,
							time: date,
						};

						loginValues.onLogin();
						values.onSubmit(existingReservation);
					}
				};

				try {
					await getActiveServices(setActiveServices);
					await getDaysOffline(setOfflineDays);
					await getServicesDaysOffline(setServiceAvailabilityException);

					const urlSearchParams = new URLSearchParams(window.location.search);
					const paymentId = urlSearchParams.get("external_reference");
					if (paymentId) {
						await getExistingReservation(paymentId);
					}
					setLoading(false);
				} catch (error) {
					setApiError(error);
				}
			};

			fetchData();
		}
	}, [isTokenFetched]);

	// Visual component, envolved by a MultistepContext provider with the information of the useMultistep hook
	// We also use a LangProvider with the translations
	// We show a loader if we don't have the information needed yet
	return (
		<MultistepContext.Provider value={values}>
			<LoginContext.Provider value={loginValues}>
				<LangProvider>
					<Box
						height="auto"
						sx={!isMobile || !!apiError ? styles.background : {}}
					>
						<Box sx={!isMobile || !!apiError ? styles.overlay : {}}>
							{apiError ? (
								<Dialog open={true}>
									<ErrorMessage error={apiError} />
								</Dialog>
							) : !loading ? (
								<Paper
									elevation={0}
									sx={
										isMobile ? styles.mobileBackground : styles.paperContainer
									}
								>
									<Main
										services={activeServices}
										setServices={setActiveServices}
										offlineDays={offlineDays}
										serviceAvailabilityException={serviceAvailabilityException}
									/>
								</Paper>
							) : (
								<Box height="auto" sx={styles.background}>
									<Loader />
								</Box>
							)}
						</Box>
					</Box>
				</LangProvider>
			</LoginContext.Provider>
		</MultistepContext.Provider>
	);
}

const styles = {
	background: {
		backgroundImage: `url(${background})`,
		backgroundSize: "cover",
		backgroundRepeat: "no-repeat",
		minHeight: "100vh",
		height: "auto",
		maxWidth: "100vw",
	},
	overlay: {
		display: "flex",
		maxWidth: "100vw",
		minHeight: "100vh",
		height: "100%",
		alignItems: "flex-start",
		justifyContent: "center",
		backgroundColor: "rgba(0, 0, 0, 0.5)",
	},
	mobileBackground: {
		backgroundColor: theme.palette.background.default,
		minHeight: "100vh",
		maxHeight: "100vh",
		pt: 1,
	},
	paperContainer: {
		backgroundColor: theme.palette.background.default,
		width: {
			xs: "90%",
			sm: "93%",
			md: "83%",
			lg: "74%",
			xl: "62%",
		},
		height: "auto",
		mt: 3,
		mb: 3,
		pb: 5,
		borderRadius: "10px",
		position: "relative",
	},
};

export default App;
